import { animated, config, useSpring, useSprings } from '@react-spring/web';
import moment from 'moment-timezone';
import { memo, useEffect, useMemo, useState } from 'react';
import CountUp from 'react-countup';

import { rewindConfig } from 'components/rewind/rewindConfig';
import { channelAttributes } from 'utils/channels';

import { useRewindContext } from '../../../rewindModal.context';
import { RewindSlideProps } from '../../../rewindModal.types';
import { SlideContainer } from '../../slideContainer';

import styles from './journeySlide.module.scss';

// Animation timing constants (in ms from slide start)
const TIMING = {
  // Phase 1: Big number alone
  COUNTER_START: 300,
  COUNTER_DURATION: 2, // seconds for countup

  // Phase 2: Number shrinks, label + average appear
  SHRINK_NUMBER: 2800,
  SHOW_LABEL: 3200,
  SHOW_AVERAGE: 3800,

  // Phase 3: Chart appears + busiest reveal
  CHART_SHOW: 5000,
  BUSIEST_REVEAL: 6500,

  // Phase 4: First & last conversations
  FIRST_CONV: 8000,
  LAST_CONV: 9500,

  // Phase 5: Closing message
  CLOSING_MESSAGE: 10500,
};

export const JourneySlide = memo(function JourneySlide({
  isCompleted,
}: RewindSlideProps): JSX.Element {
  const { data } = useRewindContext();
  const [phase, setPhase] = useState(isCompleted ? 9 : 0);
  const [showChart, setShowChart] = useState(isCompleted ?? false);

  // The average conversations handled per active day by the agent
  const averagePerDay =
    data && data.total_conversations_handled.agent > 0
      ? (
          data.total_conversations_handled.agent / data.total_active_days.agent
        ).toFixed(1)
      : '—';

  // Get the percentage of org conversations handled by this agent
  const orgPercentage = useMemo(() => {
    const percentage = data?.total_conversations_handled.percentage;
    if (!percentage || percentage <= 0) return '—';
    return `${Math.round(percentage)}%`;
  }, [data?.total_conversations_handled.percentage]);

  // Start animation sequence
  useEffect(() => {
    // If slide has completed before, skip animations
    if (isCompleted) {
      setPhase(9);
      setShowChart(true);
      return;
    }

    const timers = [
      setTimeout(() => setPhase(1), TIMING.COUNTER_START),
      setTimeout(() => setPhase(2), TIMING.SHRINK_NUMBER),
      setTimeout(() => setPhase(3), TIMING.SHOW_LABEL),
      setTimeout(() => setPhase(4), TIMING.SHOW_AVERAGE),
      setTimeout(() => {
        setPhase(5);
        setShowChart(true);
      }, TIMING.CHART_SHOW),
      setTimeout(() => setPhase(6), TIMING.BUSIEST_REVEAL),
      setTimeout(() => setPhase(7), TIMING.FIRST_CONV),
      setTimeout(() => setPhase(8), TIMING.LAST_CONV),
      setTimeout(() => setPhase(9), TIMING.CLOSING_MESSAGE),
    ];

    return () => timers.forEach(clearTimeout);
  }, [isCompleted]);

  // Prepare monthly data - memoized to prevent effect re-runs
  const allMonths = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => {
        const monthData = data?.monthly_conversation_counts.find(
          (m) => m.month === i + 1,
        );
        return {
          month: i + 1,
          count: monthData?.count || 0,
          name: moment().month(i).format('MMM'),
        };
      }),
    [data?.monthly_conversation_counts],
  );

  const maxCount = useMemo(
    () => Math.max(...allMonths.map((m) => m.count), 1),
    [allMonths],
  );

  const busiestMonth = useMemo(
    () =>
      allMonths.reduce((prev, current) =>
        prev.count > current.count ? prev : current,
      ),
    [allMonths],
  );

  // Dynamic font size based on number magnitude
  const numberSize = useMemo(() => {
    const digits = (data?.total_conversations_handled.agent ?? 0).toString()
      .length;
    if (phase < 2) {
      // Big phase - scale based on digits
      if (digits <= 2) return 220;
      if (digits <= 3) return 180;
      if (digits <= 4) return 140;
      return 110;
    }
    // Shrunk phase
    return 72;
  }, [data?.total_conversations_handled.agent, phase]);

  // Bar chart springs - animate to final position when showChart becomes true
  const barSprings = useSprings(
    12,
    allMonths.map((month, index) => ({
      heightPercent: showChart
        ? Math.max((month.count / maxCount) * 100, 3)
        : 0,
      opacity: showChart ? 1 : 0,
      delay: showChart && !isCompleted ? index * 50 : 0, // Stagger animation only when not completed
      config: { tension: 120, friction: 14 },
      immediate: isCompleted,
    })),
  );

  // Spring animations
  const numberSpring = useSpring({
    fontSize: numberSize,
    opacity: phase >= 1 ? 1 : 0,
    transform:
      phase >= 2 ? 'translateY(0px) scale(1)' : 'translateY(50px) scale(1.1)',
    config: { tension: 120, friction: 14 },
    immediate: isCompleted,
  });

  const labelSpring = useSpring({
    opacity: phase >= 3 ? 1 : 0,
    transform: phase >= 3 ? 'translateY(0px)' : 'translateY(-40px)',
    config: { tension: 120, friction: 14 },
    immediate: isCompleted,
  });

  const leftStatSpring = useSpring({
    opacity: phase >= 4 ? 1 : 0,
    transform: phase >= 4 ? 'translateX(0px)' : 'translateX(-40px)',
    config: { tension: 120, friction: 14 },
    immediate: isCompleted,
  });

  const rightStatSpring = useSpring({
    opacity: phase >= 4 ? 1 : 0,
    transform: phase >= 4 ? 'translateX(0px)' : 'translateX(40px)',
    config: { tension: 120, friction: 14 },
    delay: phase >= 4 ? 100 : 0,
    immediate: isCompleted,
  });

  const busiestIntroSpring = useSpring({
    opacity: phase >= 5 ? 1 : 0,
    transform: phase >= 5 ? 'translateY(0px)' : 'translateY(20px)',
    config: config.gentle,
    immediate: isCompleted,
  });

  const busiestRevealSpring = useSpring({
    opacity: phase >= 6 ? 1 : 0,
    transform: phase >= 6 ? 'translateX(0px)' : 'translateX(-30px)',
    config: config.gentle,
    immediate: isCompleted,
  });

  const firstConvSpring = useSpring({
    opacity: phase >= 7 ? 1 : 0,
    transform:
      phase >= 7
        ? 'translateX(0px) rotate(-1deg)'
        : 'translateX(-50px) rotate(-3deg)',
    config: { tension: 100, friction: 14 },
    immediate: isCompleted,
  });

  const lastConvSpring = useSpring({
    opacity: phase >= 8 ? 1 : 0,
    transform:
      phase >= 8
        ? 'translateX(0px) rotate(1deg)'
        : 'translateX(50px) rotate(3deg)',
    config: { tension: 100, friction: 14 },
    immediate: isCompleted,
  });

  const closingSpring = useSpring({
    opacity: phase >= 9 ? 1 : 0,
    transform: phase >= 9 ? 'translateY(0px)' : 'translateY(30px)',
    config: config.gentle,
    immediate: isCompleted,
  });

  const formatChannel = (channel: string): string => {
    const attrs = channelAttributes[channel as keyof typeof channelAttributes];
    return attrs?.name || channel.replace(/_/g, ' ');
  };

  const formatDateTime = (
    timestamp: string,
  ): { date: string; time: string } => {
    const date = moment(timestamp);
    return {
      date: date.format('LL'),
      time: date.format('LT'),
    };
  };

  return (
    <SlideContainer className={isCompleted ? styles.completed : undefined}>
      <div className={styles.content}>
        {/* Phase 1 & 2: Big counter that shrinks */}
        <div
          className={`${styles.counterSection} ${phase >= 2 ? styles.shrunk : ''}`}
        >
          <animated.div className={styles.bigNumber} style={numberSpring}>
            {phase >= 1 && (
              <CountUp
                end={data?.total_conversations_handled.agent ?? 0}
                duration={isCompleted ? 0 : TIMING.COUNTER_DURATION}
                separator=","
              />
            )}
          </animated.div>

          <animated.div className={styles.counterLabel} style={labelSpring}>
            conversations handled
          </animated.div>

          <div className={styles.statsRow}>
            <animated.div className={styles.statItem} style={leftStatSpring}>
              <span className={styles.statValue}>{averagePerDay}</span>
              <span className={styles.statLabel}>
                conversations per day on average
              </span>
            </animated.div>
            <animated.div className={styles.statItem} style={rightStatSpring}>
              <span className={styles.statValue}>{orgPercentage}</span>
              <span className={styles.statLabel}>of your company's total</span>
            </animated.div>
          </div>
        </div>

        {/* Phase 3: Busiest month section - integrated chart */}
        <div
          className={`${styles.busiestSection} ${phase >= 5 ? styles.visible : ''}`}
        >
          <div className={styles.chartContainer}>
            <animated.div
              className={styles.sectionTitle}
              style={busiestIntroSpring}
            >
              Your year in conversations
            </animated.div>

            <div className={styles.barsArea}>
              {allMonths.map((month, index) => {
                const isBusiest =
                  month.month === busiestMonth.month &&
                  showChart &&
                  busiestMonth.count > 0;
                return (
                  <div key={month.name} className={styles.barWrapper}>
                    <animated.div
                      className={`${styles.bar} ${isBusiest ? styles.busiestBar : ''} ${showChart ? styles.settled : ''}`}
                      style={{
                        opacity: barSprings[index].opacity,
                        height: barSprings[index].heightPercent.to(
                          (h) => `${h}%`,
                        ),
                      }}
                    >
                      {showChart && month.count > 0 && (
                        <span className={styles.barCount}>{month.count}</span>
                      )}
                      {/* Busiest badge appears on the bar */}
                      {isBusiest && phase >= 6 && (
                        <span className={styles.busiestBadge}>🔥</span>
                      )}
                    </animated.div>
                    <span
                      className={`${styles.monthLabel} ${isBusiest ? styles.busiestLabel : ''}`}
                    >
                      {month.name}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Busiest month callout integrated into chart */}
            <animated.div
              className={styles.busiestCallout}
              style={busiestRevealSpring}
            >
              {busiestMonth.count > 0 ? (
                <>
                  <span className={styles.busiestMonth}>
                    {moment()
                      .month(busiestMonth.month - 1)
                      .format('MMMM')}
                  </span>
                  {' was your busiest — '}
                  <span className={styles.busiestCount}>
                    {busiestMonth.count.toLocaleString()} conversations
                  </span>
                </>
              ) : (
                <>
                  You don't have any conversations in{' '}
                  <span className={styles.busiestMonth}>
                    {rewindConfig.year}
                  </span>
                </>
              )}
            </animated.div>
          </div>
        </div>

        {/* Phase 4: First & Last conversations */}
        <div
          className={`${styles.bookends} ${phase >= 7 ? styles.visible : ''}`}
        >
          <animated.div
            className={`${styles.bookend} ${styles.first}`}
            style={firstConvSpring}
          >
            <div className={styles.bookendHeader}>
              <span className={styles.bookendIcon}>🚀</span>
              <span className={styles.bookendLabel}>First conversation</span>
            </div>

            <div className={styles.bookendMain}>
              {data?.first_conversation.timestamp ? (
                <>
                  <span className={styles.bookendDate}>
                    {formatDateTime(data?.first_conversation.timestamp).date}
                  </span>
                  <span className={styles.bookendDivider}>•</span>
                  <span className={styles.bookendTime}>
                    {formatDateTime(data?.first_conversation.timestamp).time}
                  </span>
                  <span className={styles.bookendDivider}>•</span>
                  <span className={styles.bookendChannel}>
                    {formatChannel(data?.first_conversation.channel)}
                  </span>
                </>
              ) : (
                <span className={styles.bookendDate}>—</span>
              )}
            </div>
          </animated.div>

          <animated.div
            className={`${styles.bookend} ${styles.last}`}
            style={lastConvSpring}
          >
            <div className={styles.bookendHeader}>
              <span className={styles.bookendIcon}>🏁</span>
              <span className={styles.bookendLabel}>Latest conversation</span>
            </div>

            <div className={styles.bookendMain}>
              {data?.last_conversation.timestamp ? (
                <>
                  <span className={styles.bookendDate}>
                    {formatDateTime(data?.last_conversation.timestamp).date}
                  </span>
                  <span className={styles.bookendDivider}>•</span>
                  <span className={styles.bookendTime}>
                    {formatDateTime(data?.last_conversation.timestamp).time}
                  </span>
                  <span className={styles.bookendDivider}>•</span>
                  <span className={styles.bookendChannel}>
                    {formatChannel(data?.last_conversation.channel)}
                  </span>
                </>
              ) : (
                <span className={styles.bookendDate}>—</span>
              )}
            </div>
          </animated.div>
        </div>

        {/* Phase 5: Closing message */}
        <animated.div className={styles.closingMessage} style={closingSpring}>
          {(data?.total_conversations_handled.agent ?? 0) > 0
            ? '✨ Quality over quantity — you gave each conversation the attention it deserved'
            : '✨ Every journey starts somewhere — come back again next year!'}
        </animated.div>
      </div>
    </SlideContainer>
  );
});
